import api from '@/services/api';

import Vue from 'vue';

import { make } from 'vuex-pathify';

const state = {
  settings: undefined,
  settingsValues: {},
  mapping: undefined,
  additionalModules: {},
  hasMobileApp: {},
  hasQgis: false,
  mobileAppMetadata: {},
  dictionaries: [],
  modulesMapping: {},
  qgisMetadata: {},
  appKind: `${process.env.VUE_APP_APP_KIND}`,
  backendVersion: '',
  frontendVersion: `${process.env.VUE_APP_FRONT_VERSION_DATE}`,
  geoserver: {},
};

const mutations = {
  ...make.mutations(state),
  EDIT_SETTINGS_VALUE: (state, payload) => {
    const { key, value } = payload;
    const setting = state.settings.find(setting => setting.name === key);
    Vue.set(setting, 'value', value);
    Vue.set(state.settingsValues, key, value);
  },
  SET_MODULE_VALUE: (state, value) => {
    const { name, enabled } = value;
    Vue.set(state.additionalModules[name], 'enabled', enabled);
  },
  SET_MOBILE_APP_METADATA: (state, value) => {
    const { app_kind, metadata } = value;
    Vue.set(state.mobileAppMetadata, app_kind, metadata);
  },
  SET_HAS_MOBILE_APP: (state, value) => {
    const { app_kind, mobileApp } = value;
    Vue.set(state.hasMobileApp, app_kind, mobileApp);
  },
  SET_DICTIONARIES: (state, value) => {
    const dicts = value.reduce((total, current) => {
      return { ...total, [current.id]: current };
    }, {});
    state.dictionaries = { ...dicts };
  },
  DELETE_DICTIONARY: (state, value) => {
    Vue.delete(state.dictionaries, value);
  },
};

const actions = {
  async getBackendVersion({ commit }) {
    const r = await api.get('current_version');
    const value = [...r.data.data.split(':', 1), '0.0.0'];
    console.log(`Wersja back-endu: ${value[0]}, ${value[1]}`);
    commit('SET_BACKEND_VERSION', value[0]);
  },
  async checkHasImage(store, type) {
    const r = await api.get(`settings/${type}/custom`);
    return r.status === 200;
  },
  async deleteLogo(store, type) {
    const r = await api.delete(`settings/${type}`);
    return r.status === 204;
  },
  async checkHasFavicon() {
    const r = await api.get('favicon.ico/custom');
    return r.status === 200;
  },
  async deleteFavicon() {
    const r = await api.delete('favicon.ico');
    return r.status === 204;
  },
  async editSingleSetting({ commit }, payload) {
    const { key, body } = payload;
    await api.put(`settings/${key}`, body);
    commit('EDIT_SETTINGS_VALUE', { key, value: body.value });
  },
  async getSingleSetting({ commit }, key) {
    const r = await api.get(`settings/${key}`);
    commit('EDIT_SETTINGS_VALUE', { key, value: r.data.data });
  },
  async getSettings({ commit }) {
    const r = await api.get('settings');
    const settingsValues = r.data.data.reduce((t, c) => {
      return { ...t, [c.name]: c.value };
    }, {});
    commit('SET_SETTINGS', r.data.data);
    commit('SET_SETTINGS_VALUES', settingsValues);
  },
  async getPublicSettings({ commit }) {
    const r = await api.get('settings/public');
    const settingsValues = r.data.data.reduce((t, c) => {
      return { ...t, [c.name]: c.value };
    }, {});
    commit('SET_SETTINGS', r.data.data);
    commit('SET_SETTINGS_VALUES', settingsValues);
    return r;
  },
  async getMapping({ commit }) {
    const r = await api.get('app_data/mapping');
    commit('SET_MAPPING', r.data.data);
    return r;
  },
  async getModulesMapping({ commit }) {
    const r = await api.get('modules_mapping');
    commit('SET_MODULES_MAPPING', r.data.data);
  },
  async deleteAppData(store, dataSource) {
    await api.delete(`app_data/${dataSource}/features`);
  },
  async sendMail(store, payload) {
    await api.post('email/send', payload);
  },
  async getAdditionalModules({ commit }, isPublic) {
    const r = await api.get(`license_manager/modules${isPublic ? '/public' : ''}`);
    const modules = r.data.data.reduce((t, c) => {
      return { ...t, [c.name]: c };
    }, {});
    commit('SET_ADDITIONAL_MODULES', modules);
  },
  async setAdditionalModuleValue({ dispatch }, payload) {
    const { name, enabled } = payload;
    await api.post(`license_manager/modules/${name}/${enabled ? 'enable' : 'disable'}`);
    await dispatch('getSettings');
    await dispatch('users/getCurrentUser', {}, { root: true });
  },
  async getDefaultProjectValues() {
    return await api.get('projects/default');
  },
  async resetDefaultProject() {
    return await api.post('projects/default/reset');
  },
  async getDictionaries({ commit }) {
    const r = await api.get('dicts');
    commit('SET_DICTIONARIES', r.data.data);
  },
  async postDictionary({ dispatch }, formData) {
    await api.post('dicts', formData, {
      noWrap: true,
      headers: {
        'Content-Type': 'multipart/form-data;',
      },
    });
    await dispatch('getDictionaries');
  },
  async putDictionary({ dispatch }, data) {
    const { formData, id } = data;
    await api.put(`dicts/${id}`, formData, {
      noWrap: true,
      headers: {
        'Content-Type': 'multipart/form-data;',
      },
    });
    await dispatch('getDictionaries');
  },
  async deleteDictionary({ commit }, id) {
    await api.delete(`dicts/${id}`);
    commit('DELETE_DICTIONARY', id);
  },
  async importDictionary({ dispatch }, data) {
    await api.post('dicts/import/data_source', data);
    await dispatch('getDictionaries');
  },
  async putLogo(store, data) {
    await api.put('/settings/logo', data, {
      noWrap: true,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  async putFavicon(store, data) {
    await api.put('/favicon.ico', data, {
      noWrap: true,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  async putThemePicture(store, data) {
    await api.put('/settings/theme_picture', data, {
      noWrap: true,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  async getMobileMetadata({ commit }, app_kind) {
    try {
      const r = await api.get(`mobile_app/${app_kind}/latest/metadata`, {
        skipDefaultErrorHandler: true,
      });
      commit('SET_HAS_MOBILE_APP', { app_kind, mobileApp: true });
      commit('SET_MOBILE_APP_METADATA', { app_kind, metadata: r.data.data });
    } catch (e) {
      commit('SET_HAS_MOBILE_APP', { app_kind, mobileApp: false });
    }
  },
  async deleteMobileApp({ commit }, app_kind) {
    await api.delete(`mobile_app/${app_kind}`);
    commit('SET_HAS_MOBILE_APP', { app_kind, mobileApp: false });
  },
  async syncSqlite({ dispatch }) {
    await api.post('sync/sqlite', {}, { timeout: 0 });
    await dispatch('getSettings');
  },
  async syncMbtiles({ dispatch }) {
    await api.post('sync/tiles', {}, { timeout: 0 });
    await dispatch('getSettings');
  },
  async getQgisMetadata({ commit }) {
    try {
      const r = await api.get('qgis_plugins/versions?latest_only=true');
      commit('SET_HAS_QGIS', true);
      commit('SET_QGIS_METADATA', ...r.data.data);
    } catch (e) {
      commit('SET_HAS_QGIS', false);
    }
  },
  async getGeoserverMetadata({ commit }) {
    const r = await api.get('/geoserver_available');
    commit('SET_GEOSERVER', r.data.data);
  },
  async getEventsLog(store, data) {
    const { features_filter, params } = data;
    return await api.post(
      '/event_log',
      {
        features_filter,
      },
      {
        params,
      }
    );
  },
  async getEventLog(store, id) {
    return await api.get(`/event_log/${id}`);
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
